import type { Currency } from "@loot-os/api-contract";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { useCurrency } from "../../../../lib/currency.js";
import { boxesQueryOptions } from "../../../../lib/queries.js";
import { useTerminology } from "../../../../lib/terminology.js";
import { type BoxTile, useBoxTiles } from "../../shared/boxTiles.js";

export interface FeaturedBoxesContent {
  title: string;
  currency: Currency;
  /** The boxes a tenant editor marked `boxes.featured`; null until the list has loaded. */
  tiles: BoxTile[] | null;
  /** Why the list failed to load; null when it did not. */
  errorMessage: string | null;
  /**
   * Render nothing at all: nothing is featured, a merchandising choice rather than an empty
   * store. Never true while loading or after a failure — a row that vanished on a failed
   * request would read as "nothing is featured".
   */
  hidden: boolean;
}

/** The featured row: the boxes marked `featured`, out of everything GET /v1/boxes returns. */
export function useFeaturedBoxesContent(): FeaturedBoxesContent {
  const currency = useCurrency();
  const { box } = useTerminology();
  const query = useQuery(boxesQueryOptions());
  const featured = useMemo(() => query.data?.filter((b) => b.featured) ?? null, [query.data]);
  const tiles = useBoxTiles(featured);
  const error = query.error
    ? query.error instanceof Error
      ? query.error.message
      : `failed to load ${box.plural}`
    : null;

  return {
    title: `Featured ${box.plural}`,
    currency,
    tiles,
    errorMessage: error ? error : null,
    hidden: tiles?.length === 0 && !error,
  };
}
